import type { DeepContent } from "@/components/deep-content";

/**
 * Long-form guidance appended beneath each calculator.
 * The national-minimum-wage-calculator has its own bespoke component
 * (NMWDeepContent) and is deliberately absent here.
 */
export const calculatorDepth: Record<string, DeepContent> = {
  "redundancy-pay-calculator": {
    title: "How statutory redundancy pay is calculated",
    intro:
      "Statutory redundancy pay is a formula, not a negotiation. It uses three inputs: age during each year of service, complete years of continuous service, and a weekly pay figure that is capped by statute.",
    sections: [
      {
        heading: "The formula",
        blocks: [
          {
            kind: "table",
            head: ["Age during the year of service", "Weeks' pay for that year"],
            rows: [
              ["Under 22", "Half a week's pay"],
              ["22 to 40", "One week's pay"],
              ["41 and over", "One and a half weeks' pay"],
            ],
          },
          {
            kind: "list",
            items: [
              "Only complete years count, and service is capped at 20 years, counting back from the date of dismissal.",
              "A week's pay is capped at the statutory limit that applies on the date of termination, so high earners receive the capped figure.",
              "Employees need two years' continuous service to qualify at all.",
              "Statutory redundancy pay is free of income tax and National Insurance, and it counts towards the £30,000 termination payment exemption.",
            ],
          },
        ],
      },
      {
        heading: "Working out a week's pay correctly",
        blocks: [
          {
            kind: "list",
            items: [
              "Fixed hours and fixed pay: use the contractual gross weekly pay at the calculation date.",
              "Variable hours or output pay: average the gross pay over the 12 weeks ending with the last complete week before dismissal, ignoring weeks with no pay and going further back to make up twelve.",
              "Overtime counts only where it is guaranteed and the employee is obliged to work it.",
              "Benefits in kind, discretionary bonuses and expenses are excluded.",
              "Where an employee was on reduced pay because of sickness or family leave, use the pay they would have received working normally.",
            ],
          },
        ],
      },
      {
        heading: "Continuous service: what breaks it and what does not",
        blocks: [
          {
            kind: "list",
            items: [
              "Sickness, holiday, maternity and other family leave do not break continuity.",
              "A TUPE transfer preserves continuity, so service with the previous employer counts.",
              "A genuine break in employment of a full week running Sunday to Saturday can break continuity, but re-engagement within four weeks after redundancy may preserve it.",
              "Strike days do not break continuity but are deducted from the total service.",
            ],
          },
          {
            kind: "callout",
            title: "Enhanced schemes",
            text: "If your contract, handbook or established custom promises more than the statutory minimum, that enhanced figure is contractual and enforceable. Check before you issue letters, because withdrawing an enhancement later is a breach of contract claim.",
          },
        ],
      },
      {
        heading: "When redundancy pay is not due",
        blocks: [
          {
            kind: "list",
            items: [
              "Less than two years' continuous service at the date of dismissal.",
              "The employee unreasonably refuses an offer of suitable alternative employment.",
              "The employee resigns before the notice period expires without agreeing an early leaving date.",
              "The dismissal is genuinely for conduct rather than redundancy, though dismissing for conduct mid-redundancy invites a claim and should not be done without advice.",
            ],
          },
        ],
      },
      {
        heading: "Paying and recording it",
        blocks: [
          {
            kind: "list",
            ordered: true,
            items: [
              "Give the employee a written statement showing how the payment was calculated.",
              "Pay it on or shortly after the termination date, and separately identify notice pay and holiday pay, which are taxable.",
              "Keep the scoring, consultation notes and calculation together in one file for at least six years.",
            ],
          },
        ],
      },
    ],
  },

  "holiday-entitlement-calculator": {
    title: "UK holiday entitlement rules for employers",
    intro:
      "The statutory minimum is 5.6 weeks per leave year, which is 28 days for someone working five days a week. Bank holidays can be counted within that 28 days unless the contract says otherwise. The complications come from part-time, irregular hours and mid-year starters.",
    sections: [
      {
        heading: "The building blocks",
        blocks: [
          {
            kind: "table",
            head: ["Working pattern", "Statutory entitlement"],
            rows: [
              ["5 days a week", "28 days, which may include bank holidays"],
              ["4 days a week", "22.4 days"],
              ["3 days a week", "16.8 days"],
              ["Shifts of varying length", "5.6 weeks expressed in hours, using the average working week"],
              ["Irregular hours and part-year workers", "Accrual of 12.07% of hours worked in each pay period, for leave years beginning on or after 1 April 2024"],
            ],
          },
          {
            kind: "p",
            text: "The 28-day statutory maximum applies to full-time staff, so an employee working six days a week is still only entitled to 28 days by statute. Entitlement above the statutory floor is contractual and you can attach your own rules to it.",
          },
        ],
      },
      {
        heading: "Starters, leavers and pro-rata",
        blocks: [
          {
            kind: "list",
            ordered: true,
            items: [
              "Work out the full-year entitlement for the working pattern.",
              "Multiply by the proportion of the leave year the employee is employed for.",
              "Round part days up in the employee's favour, or apply your policy consistently if it is more generous.",
              "On leaving, compare leave taken against leave accrued and pay any balance in the final pay run.",
              "Only recover overtaken leave where the contract contains an express deduction clause.",
            ],
          },
        ],
      },
      {
        heading: "Holiday pay: what must be included",
        blocks: [
          {
            kind: "list",
            items: [
              "The four weeks derived from EU-retained law must be paid at normal remuneration, including regular overtime, commission and shift premiums.",
              "The additional 1.6 weeks can be paid at basic pay unless the contract says otherwise, though many employers pay everything at the higher rate for simplicity.",
              "Use a 52-week reference period, ignoring weeks with no pay, when averaging variable pay.",
              "Rolled-up holiday pay is lawful again for irregular hours and part-year workers from April 2024, at 12.07% shown separately on the payslip. It remains unlawful for other workers.",
            ],
          },
        ],
      },
      {
        heading: "Carry-over and refusals",
        blocks: [
          {
            kind: "list",
            items: [
              "Up to 8 days of the additional entitlement may be carried over by agreement.",
              "Leave untaken because of long-term sickness carries over for up to 18 months after the end of the leave year.",
              "Leave untaken because of maternity or other family leave must be allowed to carry over in full.",
              "You can refuse a request by giving counter-notice at least as long as the leave requested, and you can require leave to be taken by giving twice the notice.",
            ],
          },
          {
            kind: "callout",
            title: "Record keeping",
            text: "Keep the leave year dates, the calculation method and the running balance for every worker. Most holiday pay disputes are won or lost on whether the employer can show the working out.",
          },
        ],
      },
    ],
  },

  "ssp-calculator": {
    title: "Statutory Sick Pay rules employers get wrong",
    intro:
      "SSP is a flat weekly rate paid for qualifying days of sickness absence. Most errors come from waiting days, linked periods and the earnings threshold rather than the rate itself.",
    sections: [
      {
        heading: "Who qualifies",
        blocks: [
          {
            kind: "list",
            items: [
              "The individual must be classed as an employee and have done some work under the contract.",
              "Average weekly earnings must be at or above the lower earnings limit, calculated over the eight weeks before the sickness began.",
              "The absence must be for four or more consecutive days, including non-working days, which forms a period of incapacity for work.",
              "The employee must tell you within your notification deadline, or seven days if you have not set one.",
            ],
          },
        ],
      },
      {
        heading: "Waiting days, qualifying days and linking",
        blocks: [
          {
            kind: "list",
            ordered: true,
            items: [
              "The first three qualifying days are waiting days and are unpaid.",
              "Qualifying days are normally the days the employee usually works, agreed in advance and applied consistently.",
              "Two periods of sickness eight weeks apart or less link together, so waiting days are not served again.",
              "SSP is payable for up to 28 weeks in any period of entitlement.",
              "When SSP is close to running out, issue form SSP1 so the employee can claim state benefits.",
            ],
          },
        ],
      },
      {
        heading: "Evidence and fit notes",
        blocks: [
          {
            kind: "p",
            text: "You can ask for self-certification for the first seven calendar days, and a fit note from a GP or other approved healthcare professional after that. A fit note recommending an amended role, altered hours, a phased return or workplace adaptations should be treated as a starting point for discussion, not as an instruction you must follow in full.",
          },
          {
            kind: "p",
            text: "Where the absence relates to a condition that could be a disability under the Equality Act 2010, consider an occupational health referral and reasonable adjustments early. Managing capability without medical evidence is the most common route to a discrimination claim.",
          },
        ],
      },
      {
        heading: "Company sick pay and absence management",
        blocks: [
          {
            kind: "list",
            items: [
              "Contractual sick pay usually absorbs SSP rather than being paid on top. Say so clearly in the contract.",
              "Trigger points such as the Bradford Factor are useful, but always allow discretion for disability-related and pregnancy-related absence.",
              "Hold return-to-work conversations after every absence. They deter casual absence and surface underlying issues.",
              "Keep absence records for at least three years, and treat medical information as special category data under UK GDPR.",
            ],
          },
        ],
      },
    ],
  },

  "maternity-pay-calculator": {
    title: "Statutory Maternity Pay from notification to return",
    intro:
      "SMP runs for up to 39 weeks: six weeks at 90% of average weekly earnings, then 33 weeks at the standard rate or 90% of average weekly earnings if that is lower. Maternity leave itself lasts up to 52 weeks regardless of pay.",
    sections: [
      {
        heading: "Qualifying conditions",
        blocks: [
          {
            kind: "list",
            items: [
              "26 weeks' continuous service by the end of the qualifying week, which is the 15th week before the expected week of childbirth.",
              "Average weekly earnings at or above the lower earnings limit over the eight weeks ending with the qualifying week.",
              "Notice of the pregnancy and intended start date at least 28 days before SMP is to begin.",
              "Medical evidence, normally the MATB1 certificate issued from 20 weeks before the expected week of childbirth.",
            ],
          },
          {
            kind: "p",
            text: "An employee who does not qualify for SMP may still qualify for Maternity Allowance. Issue form SMP1 within seven days of deciding so they can claim without delay.",
          },
        ],
      },
      {
        heading: "Leave, pay and the key dates",
        blocks: [
          {
            kind: "table",
            head: ["Period", "What applies"],
            rows: [
              ["Weeks 1 to 6", "90% of average weekly earnings with no cap"],
              ["Weeks 7 to 39", "The lower of the standard SMP rate and 90% of average weekly earnings"],
              ["Weeks 40 to 52", "Additional maternity leave, unpaid unless the contract provides more"],
              ["First two weeks after birth", "Compulsory maternity leave, four weeks in factory work, during which the employee must not work"],
            ],
          },
        ],
      },
      {
        heading: "Rights during and after leave",
        blocks: [
          {
            kind: "list",
            items: [
              "All contractual terms except pay continue, so holiday continues to accrue and pension contributions continue on notional pay during paid leave.",
              "Up to ten keeping in touch days may be worked by agreement without ending leave. Neither side can insist on them.",
              "The employee returns to the same job after ordinary maternity leave, or to a suitable and appropriate alternative after additional maternity leave where returning to the same job is not reasonably practicable.",
              "Redundancy protection now extends from the point the employer is told of the pregnancy until 18 months after the birth, giving priority for suitable alternative vacancies.",
              "Eight weeks' notice is required if the employee wants to change their return date.",
            ],
          },
          {
            kind: "callout",
            title: "Enhanced maternity pay",
            text: "If you enhance maternity pay, set out clearly whether repayment is required if the employee does not return, and apply the policy consistently. Compare it against your shared parental leave policy to reduce the risk of a sex discrimination argument.",
          },
        ],
      },
    ],
  },

  "paternity-pay-calculator": {
    title: "Statutory Paternity Pay and leave in practice",
    intro:
      "Paternity leave is up to two weeks, paid at the standard statutory rate or 90% of average weekly earnings if lower. Since April 2024 the two weeks can be taken as two separate one-week blocks at any point in the first year.",
    sections: [
      {
        heading: "Who qualifies",
        blocks: [
          {
            kind: "list",
            items: [
              "26 weeks' continuous service by the end of the 15th week before the expected week of childbirth, or the week the adoption match is notified.",
              "Average weekly earnings at or above the lower earnings limit.",
              "The employee is the biological father, the mother's or adopter's spouse, civil partner or partner, or the intended parent in a surrogacy arrangement.",
              "They have or expect to have responsibility for the child's upbringing.",
            ],
          },
        ],
      },
      {
        heading: "Notice and flexibility",
        blocks: [
          {
            kind: "list",
            ordered: true,
            items: [
              "Notice of entitlement is given by the end of the 15th week before the expected week of childbirth.",
              "28 days' notice is required for each period of leave, and that notice can be varied with the same notice period.",
              "Leave can be taken as one block of two weeks, or two separate blocks of one week.",
              "All leave must be taken within 52 weeks of the birth or placement.",
              "Employees who do not qualify for pay may still qualify for leave, and vice versa in rare cases.",
            ],
          },
        ],
      },
      {
        heading: "How it interacts with other family leave",
        blocks: [
          {
            kind: "list",
            items: [
              "Paternity leave cannot be taken after shared parental leave has been taken for the same child.",
              "Shared parental leave allows up to 50 weeks of leave and 37 weeks of pay to be split between parents, and needs its own notice regime.",
              "Unpaid parental leave gives 18 weeks per child up to their 18th birthday, in blocks of a week, capped at four weeks per child per year.",
              "Employees are protected from detriment or dismissal for taking or seeking to take family leave, and such dismissals are automatically unfair with no qualifying service.",
            ],
          },
          {
            kind: "callout",
            title: "Policy tip",
            text: "Set out paternity, shared parental and enhanced pay in a single family leave policy. Fragmented policies are the main reason managers give inconsistent answers, which is where discrimination risk begins.",
          },
        ],
      },
    ],
  },

  "notice-period-calculator": {
    title: "Notice periods: statutory minimums and contractual terms",
    intro:
      "Section 86 of the Employment Rights Act 1996 sets a floor for notice. The contract can improve on it but never reduce it. Getting notice wrong turns an otherwise fair dismissal into a wrongful dismissal claim.",
    sections: [
      {
        heading: "Statutory minimum notice",
        blocks: [
          {
            kind: "table",
            head: ["Continuous service", "Notice from employer", "Notice from employee"],
            rows: [
              ["Less than 1 month", "None under statute", "None under statute"],
              ["1 month to under 2 years", "1 week", "1 week"],
              ["2 years to 12 years", "1 week per complete year", "1 week"],
              ["12 years or more", "12 weeks (the cap)", "1 week"],
            ],
          },
          {
            kind: "p",
            text: "Employees only ever owe one week under statute, however long they have served. If you want longer notice from senior staff, it must be written into the contract and kept reasonable, because unreasonably long notice is hard to enforce in practice.",
          },
        ],
      },
      {
        heading: "Serving notice properly",
        blocks: [
          {
            kind: "list",
            ordered: true,
            items: [
              "Put it in writing and state the termination date, not just the length.",
              "Check when the contract says notice takes effect: on posting, on delivery, or on receipt.",
              "Notice does not start on the day it is given unless the contract says so, so count from the following day where the contract is silent.",
              "Confirm whether notice will be worked, spent on garden leave, or paid in lieu.",
              "Set out final pay, accrued holiday, benefit end dates and the return of company property in the same letter.",
            ],
          },
        ],
      },
      {
        heading: "Pay during notice",
        blocks: [
          {
            kind: "list",
            items: [
              "Where the contractual notice is at least one week longer than the statutory minimum, ordinary contractual pay rules apply, so an employee on unpaid sick leave may receive nothing.",
              "Where contractual notice equals or is less than one week above the statutory minimum, the employee is entitled to full pay during notice even if they are sick, on maternity leave, or laid off.",
              "Holiday continues to accrue during worked notice and garden leave.",
              "Payment in lieu is taxable in full as post-employment notice pay.",
            ],
          },
          {
            kind: "callout",
            title: "Summary dismissal",
            text: "The only situation where no notice is due is a genuine gross misconduct dismissal following a fair process. Getting that wrong gives the employee a wrongful dismissal claim for the notice value on top of any unfair dismissal claim.",
          },
        ],
      },
    ],
  },

  "unfair-dismissal-calculator": {
    title: "How tribunals value an unfair dismissal claim",
    intro:
      "Awards have two main parts. The basic award mirrors statutory redundancy pay. The compensatory award covers actual financial loss, which is where most of the value sits and where the biggest variables are.",
    sections: [
      {
        heading: "The basic award",
        blocks: [
          {
            kind: "list",
            items: [
              "Half a week's pay for each year of service under 22, one week from 22 to 40, and one and a half weeks from 41.",
              "Capped at 20 years of service and at the statutory weekly pay limit.",
              "Reduced by any statutory redundancy payment already made, and by any conduct that makes a reduction just and equitable.",
            ],
          },
        ],
      },
      {
        heading: "The compensatory award",
        blocks: [
          {
            kind: "list",
            items: [
              "Immediate loss of earnings from dismissal to the hearing, net of pay from any new job.",
              "Future loss, based on how long the tribunal thinks it will take to find comparable work.",
              "Loss of statutory rights, conventionally a modest fixed sum, plus pension loss which can be substantial in defined benefit schemes.",
              "Capped at the lower of the statutory maximum or 52 weeks' gross pay, except in whistleblowing and health and safety dismissals where there is no cap.",
            ],
          },
        ],
      },
      {
        heading: "Adjustments that move the number",
        blocks: [
          {
            kind: "table",
            head: ["Adjustment", "Effect"],
            rows: [
              ["ACAS Code uplift or reduction", "Up to 25% either way for unreasonable failure to follow the Code"],
              ["Polkey reduction", "Percentage reduction reflecting the chance the employee would have been dismissed anyway with a fair process"],
              ["Contributory fault", "Reduction where the employee's conduct caused or contributed to the dismissal"],
              ["Failure to mitigate", "Reduction where the employee did not take reasonable steps to find new work"],
              ["Discrimination claims running alongside", "Uncapped, plus injury to feelings under the Vento bands and possible interest"],
            ],
          },
        ],
      },
      {
        heading: "The real cost of defending a claim",
        blocks: [
          {
            kind: "p",
            text: "Costs orders are rare in the employment tribunal, so each side normally bears its own legal fees. A defended claim reaching a three-day final hearing typically takes 12 to 18 months and consumes significant management time in disclosure, witness statements and preparation.",
          },
          {
            kind: "p",
            text: "This is why commercial settlement often makes sense even in a defensible case. ACAS early conciliation is free, confidential, and available before a claim is issued, and a settlement agreement gives you a clean waiver of claims.",
          },
          {
            kind: "callout",
            title: "Use the estimate as a range",
            text: "The estimator above gives an indicative exposure. It cannot price the strength of your process, the credibility of witnesses, or any discrimination element, all of which shift the number significantly. Treat it as the starting point for a conversation, not a settlement figure.",
          },
        ],
      },
    ],
  },
};

export function getCalculatorDepth(slug: string): DeepContent | undefined {
  return calculatorDepth[slug];
}
